import { Flex, FormControl, Heading } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { useCallback, useMemo } from "react";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { JsonEditor } from "src/scenes/admin/components/JsonEditor";
import * as OrgService from "src/services/orgService";
import * as RD from "src/types/remoteData";
import { z } from "zod";

const emailLoginSchema = {
  type: "object",
  additionalProperties: false,
  required: ["method"],
  properties: {
    method: { type: "string", enum: ["email"] },
  },
};

const smsLoginSchema = {
  type: "object",
  additionalProperties: false,
  required: ["method"],
  properties: {
    method: { type: "string", enum: ["sms"] },
  },
};

const ssoLoginSchema = {
  type: "object",
  additionalProperties: false,
  required: ["method", "name", "identityProvider"],
  properties: {
    method: { type: "string", enum: ["sso"] },
    name: { type: "string", minLength: 1 },
    identityProvider: { type: "string", minLength: 1 },
  },
};

const loginConfigJsonSchema = {
  type: "object",
  additionalProperties: false,
  propertyNames: { minLength: 1 },
  required: ["admin", "parent"],
  properties: {
    disabled: { type: "boolean" },
    parentDefaultMethod: {
      type: "string",
      enum: ["sso", "email", "sms"],
      description:
        "Preferred parent login method. Must be one of the parent methods. When omitted, the field stays unset.",
    },
    admin: {
      type: "array",
      items: { oneOf: [emailLoginSchema, ssoLoginSchema] },
    },
    parent: {
      type: "array",
      items: { oneOf: [emailLoginSchema, smsLoginSchema, ssoLoginSchema] },
    },
  },
};

export const Form = () => {
  const formik = useFormikContext<FormikType>();
  return (
    <Flex direction="column" gap={2}>
      <Heading as="h3" variant="admin">
        Login
      </Heading>
      <FormikSwitchableContent<"enabled" | "disabled">
        title="Login"
        label="Login methods"
        description="Configure the login methods for your organization. parentDefaultMethod is optional. When set, it must be one of the parent methods."
        checked={["enabled", ""]}
        unchecked={["disabled", ""]}
        showContent={(value) => value === "enabled"}
        name="login.status"
      >
        <FormControl
          // isInvalid={!!meta.error}
          height="400px"
          display="flex"
          flexDirection="column"
        >
          <JsonEditor
            hideToolbar
            height="100%"
            schema={loginConfigJsonSchema}
            filename="Login.json"
            text={formik.values.login.json}
            onChange={(newValue: any) => {
              formik.setFieldValue("login.json", newValue);
            }}
          />
          {/* {meta.error && <FormErrorMessage>Invalid config</FormErrorMessage>} */}
        </FormControl>
      </FormikSwitchableContent>
    </Flex>
  );
};

export function useForm(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const { remoteData, update } =
    OrgService.useOrgConfig<OrgConfig.LoginConfig>("Login");
  const initialValues = useMemo(() => {
    return remoteData.map((config): FormikType => {
      if (config?.disabled === false) {
        return {
          login: {
            status: "enabled",
            json: loginConfigJson(config),
          },
        };
      }
      return {
        login: {
          status: "disabled",
          json: loginConfigJson({
            parent: OrgConfig.LoginConfigSchema.default.parent,
            admin: OrgConfig.LoginConfigSchema.default.admin,
          }),
        },
      };
    });
  }, [remoteData]);

  const submit = useCallback(
    async (values: FormikType) => {
      const json = JSON.parse(values.login.json);

      if (values.login.status === "enabled") {
        await update({
          disabled: false,
          ...json,
        });
      } else {
        await update({
          admin: [...OrgConfig.LoginConfigSchema.default.admin],
          parent: [...OrgConfig.LoginConfigSchema.default.parent],
        });
      }
    },
    [update]
  );
  return { initialValues, submit };
}

function loginConfigJson(config: {
  parent: readonly { method?: string }[];
  admin: readonly unknown[];
  parentDefaultMethod?: string | null;
}): string {
  return JSON.stringify(
    {
      parent: config.parent,
      admin: config.admin,
      ...(config.parentDefaultMethod
        ? { parentDefaultMethod: config.parentDefaultMethod }
        : {}),
    },
    null,
    2
  );
}

export const FormikSchema = z.object({
  login: z.object({
    status: z.union([z.literal("enabled"), z.literal("disabled")]),
    json: z.string(),
  }),
});

type FormikType = z.infer<typeof FormikSchema>;
